/*
 * WorkAndBuhin.vue の外部画像ロード状態と socket 画像更新反映を切り出した処理。
 */
import { ExtendsImageInspectVO } from "@/model/ExtendsImage";
import { MESSAGE } from "@/model/Message";

export interface ExtendsImageInspectLoadState {
  shouldLoad: boolean;
  sknUketsukeInspectId: any;
}

export interface ExtendsImageInspectLoadActionState {
  shouldLoad: boolean;
  shouldConnectSocket: boolean;
  sknUketsukeInspectId: any;
}

export interface ExtendsImageInspectLoadApplyEffectState {
  shouldApplyInspect: boolean;
  extendsImageInspect: ExtendsImageInspectVO | null;
  shouldConnectSocket: boolean;
}

export interface ExtendsImageSocketUpdateState {
  shouldApply: boolean;
  shouldShowMessage: boolean;
  extendsImageInspect: ExtendsImageInspectVO | null;
}

export interface ExtendsImageSocketApplyActionState {
  shouldApplyInspect: boolean;
  shouldShowMessage: boolean;
  extendsImageInspect: ExtendsImageInspectVO | null;
}

export interface ExtendsImageSocketApplyEffectState {
  shouldApplyInspect: boolean;
  shouldShowMessage: boolean;
  messagePayload: any;
  extendsImageInspect: ExtendsImageInspectVO | null;
}

/*
 * buildExtendsImageInspectLoadState を WorkAndBuhin.vue から分離し、
 * 画像連携のロード・socket 判定 を親側置換や単体テストで固定するため。
 */
export function buildExtendsImageInspectLoadState(params: {
  isCanExtendsImage: boolean;
  data: any;
}): ExtendsImageInspectLoadState {
  if (!params.isCanExtendsImage || !params.data.sknInspectDescriptionId) {
    return {
      shouldLoad: false,
      sknUketsukeInspectId: null
    };
  }
  return {
    shouldLoad: true,
    sknUketsukeInspectId: params.data.sknUketsukeInspectId
  };
}

/*
 * buildExtendsImageInspectLoadActionState を WorkAndBuhin.vue から分離し、
 * 画像取得と socket 接続の実行条件を親側置換や単体テストで固定するため。
 */
export function buildExtendsImageInspectLoadActionState(params: {
  isCanExtendsImage: boolean;
  data: any;
}): ExtendsImageInspectLoadActionState {
  const loadState = buildExtendsImageInspectLoadState(params);
  return {
    shouldLoad: loadState.shouldLoad,
    shouldConnectSocket: loadState.shouldLoad,
    sknUketsukeInspectId: loadState.sknUketsukeInspectId
  };
}

/*
 * buildExtendsImageInspectLoadApplyEffectState を WorkAndBuhin.vue から分離し、
 * 画像取得後の画像 state 反映と socket 接続条件を固定するため。
 */
export function buildExtendsImageInspectLoadApplyEffectState(params: {
  actionState: ExtendsImageInspectLoadActionState;
  extendsImageInspect: ExtendsImageInspectVO | null;
}): ExtendsImageInspectLoadApplyEffectState {
  return {
    shouldApplyInspect: params.actionState.shouldLoad,
    extendsImageInspect: params.extendsImageInspect,
    shouldConnectSocket: params.actionState.shouldConnectSocket
  };
}

/*
 * resolveExtendsImageSocketUpdateState を WorkAndBuhin.vue から分離し、
 * 画像連携のロード・socket 判定 を親側置換や単体テストで固定するため。
 */
export function resolveExtendsImageSocketUpdateState(params: {
  currentInspect: ExtendsImageInspectVO | null;
  updatedList: ExtendsImageInspectVO[];
}): ExtendsImageSocketUpdateState {
  const updatedInspect = params.updatedList[0];
  if (
    params.currentInspect &&
    updatedInspect.sknUketsukeInspectId === params.currentInspect.sknUketsukeInspectId
  ) {
    return {
      shouldApply: true,
      shouldShowMessage: true,
      extendsImageInspect: updatedInspect
    };
  }
  return {
    shouldApply: false,
    shouldShowMessage: false,
    extendsImageInspect: params.currentInspect
  };
}

/*
 * buildExtendsImageSocketApplyActionState を WorkAndBuhin.vue から分離し、
 * socket 更新時の画像 state 反映と message 表示条件を親側置換や単体テストで固定するため。
 */
export function buildExtendsImageSocketApplyActionState(params: {
  currentInspect: ExtendsImageInspectVO | null;
  updatedList: ExtendsImageInspectVO[];
}): ExtendsImageSocketApplyActionState {
  const updateState = resolveExtendsImageSocketUpdateState(params);
  return {
    shouldApplyInspect: updateState.shouldApply,
    shouldShowMessage: updateState.shouldShowMessage,
    extendsImageInspect: updateState.extendsImageInspect
  };
}

/*
 * buildExtendsImageSocketApplyEffectState を WorkAndBuhin.vue から分離し、
 * socket 画像更新時の message payload と画像 state 反映条件を固定するため。
 */
export function buildExtendsImageSocketApplyEffectState(
  actionState: ExtendsImageSocketApplyActionState
): ExtendsImageSocketApplyEffectState {
  return {
    shouldApplyInspect: actionState.shouldApplyInspect,
    shouldShowMessage: actionState.shouldShowMessage,
    messagePayload: {
      message: MESSAGE.UpdatedImage,
      type: "info",
      duration: 700,
      customClass: "msg-center shadow",
      showClose: true
    },
    extendsImageInspect: actionState.extendsImageInspect
  };
}
